import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import { PushNotifications } from '@capacitor/push-notifications';
import { Platform } from '@ionic/angular';

import { MessagePayload, NotificationPayload, getMessaging, getToken, isSupported, onMessage } from 'firebase/messaging';
import { BehaviorSubject, Observable, of } from 'rxjs';

import { EnvironmentConfig } from '../config/environment.config';
import { FirebaseConfig } from '../models/firebase-config.interface';

@Injectable({ providedIn: 'root' })
export class NotificationsService {
  message$ = new BehaviorSubject<NotificationPayload | null>(null);

  constructor(@Inject('environment') private environment: EnvironmentConfig, private http: HttpClient, private plf: Platform) {}

  init(): void {
    this.initNotifications(this.environment.firebase);
  }

  listen(): void {
    const messaging = getMessaging();
    onMessage(messaging, (payload: MessagePayload) => {
      this.message$.next(payload.notification);
    });
  }

  dismissMessage(): void {
    this.message$.next(null);
  }

  private initNotifications(config: FirebaseConfig): void {
    this.plf.ready().then(() => {
      if (Capacitor.getPlatform() !== 'web') {
        this.registerAsyncPush();
      } else {
        isSupported().then((supported) => {
          if (supported) {
            this.requestPermission(config.vapidKey);
            this.listen();
          }
        });
      }
    });
  }

  private async requestPermission(vapidKey: string): Promise<void> {
    const messaging = getMessaging();
    const serviceWorkerRegistration = await navigator.serviceWorker.register('/firebase-messaging-sw.js', {
      scope: '/'
    });

    getToken(messaging, {
      vapidKey,
      serviceWorkerRegistration
    })
      .then((currentToken) => {
        if (currentToken) {
          this.sendToken(currentToken).subscribe();
        }
      })
      .catch(() => {});
  }

  private registerAsyncPush(): void {
    const addListeners = async (): Promise<void> => {
      await PushNotifications.addListener('registration', (token) => {
        this.sendToken(token.value).subscribe();
      });

      await PushNotifications.addListener('registrationError', (err) => {
        console.error('Push Notification Registration Error: ', err);
      });

      await PushNotifications.addListener('pushNotificationReceived', () => {});

      await PushNotifications.addListener('pushNotificationActionPerformed', () => {});
    };

    const registerNotifications = async (): Promise<void> => {
      let permStatus = await PushNotifications.checkPermissions();

      // TODO Android notifications always returns true for check permissions. use local-notifications

      if (permStatus.receive === 'prompt') {
        permStatus = await PushNotifications.requestPermissions();
      }

      await PushNotifications.register();
    };

    addListeners();
    registerNotifications();
  }

  private sendToken(value: string): Observable<unknown> {
    this.plf.ready().then(() => {
      const platform = Capacitor.getPlatform();
      this.http
        .put(`${this.environment.endPoint}panel-member/notification/token`, {
          type: platform,
          token: value
        })
        .subscribe();
    });
    return of([]);
  }
}
